import { Component } from "@theme/component";
import { isClickedOutside, onAnimationEnd } from "@theme/dom-events";
import { debounce } from "@theme/scheduling";
class DialogComponent extends Component {
  requiredRefs = ["dialog"];
  connectedCallback() {
    super.connectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.addEventListener("resize", this.#handleResize);
    }
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener("resize", this.#handleResize);
    }
  }
  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;
    if (!minWidth && !maxWidth) return;
    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);
  #previousScrollY = 0;
  #inertedElements = [];
  /**
   * Marks every body child except this component as `inert` so keyboard
   * focus and pointer events are trapped inside the non-modal dialog.
   * Native `<dialog>` modal mode does this automatically; non-modal mode
   * does not, so we emulate it.
   */
  #applyInertOutside() {
    this.#inertedElements = [];
    const dialogTop = parseFloat(getComputedStyle(this.refs.dialog).top) || 0;
    for (const el of Array.from(document.body.children)) {
      if (el === this) continue;
      if (el.hasAttribute("inert")) continue;
      if (el.hasAttribute("data-dialog-passthrough")) continue;
      if (dialogTop > 0 && el.id === "header-group") continue;
      el.setAttribute("inert", "");
      this.#inertedElements.push(el);
    }
  }
  #removeInertOutside() {
    for (const el of this.#inertedElements) {
      el.removeAttribute("inert");
    }
    this.#inertedElements = [];
  }
  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;
    if (dialog.open) return;
    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;
    const nonModal = this.hasAttribute("data-non-modal");
    requestAnimationFrame(() => {
      document.body.style.width = "100%";
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      if (nonModal) {
        dialog.show();
        this.#applyInertOutside();
        const firstFocusable = dialog.querySelector(
          'a, button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        firstFocusable?.focus();
      } else {
        dialog.showModal();
      }
      this.dispatchEvent(new DialogOpenEvent());
      if (nonModal) {
        document.addEventListener("click", this.#handleDocumentClick, true);
        document.addEventListener("keydown", this.#handleKeyDown);
      } else {
        this.addEventListener("click", this.#handleClick);
        this.addEventListener("keydown", this.#handleKeyDown);
      }
    });
  }
  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;
    if (!dialog.open) return;
    if (dialog.classList.contains("dialog-closing")) return;
    this.removeEventListener("click", this.#handleClick);
    this.removeEventListener("keydown", this.#handleKeyDown);
    document.removeEventListener("click", this.#handleDocumentClick, true);
    document.removeEventListener("keydown", this.#handleKeyDown);
    dialog.style.animation = "none";
    void dialog.offsetWidth;
    dialog.classList.add("dialog-closing");
    dialog.style.animation = "";
    await onAnimationEnd(dialog, void 0, {
      subtree: false
    });
    document.body.style.width = "";
    document.body.style.position = "";
    document.body.style.top = "";
    window.scrollTo({ top: this.#previousScrollY, behavior: "instant" });
    dialog.close();
    dialog.classList.remove("dialog-closing");
    this.#removeInertOutside();
    this.dispatchEvent(new DialogCloseEvent());
  };
  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };
  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;
    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }
  /**
   * Closes a non-modal dialog when the user clicks outside it. Skips clicks on
   * the trigger so the trigger's own toggle handler can take over (otherwise
   * the dialog would close here, then re-open on the trigger click).
   */
  #handleDocumentClick = (event) => {
    const { dialog } = this.refs;
    const target = event.target;
    if (!target) return;
    if (dialog.contains(target)) return;
    const triggerId = this.id;
    if (triggerId && target.closest(`[aria-controls="${CSS.escape(triggerId)}"]`)) {
      return;
    }
    this.closeDialog();
  };
  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== "Escape") return;
    event.preventDefault();
    this.closeDialog();
  }
  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute("dialog-active-min-width"));
  }
  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute("dialog-active-max-width"));
  }
}
if (!customElements.get("dialog-component"))
  customElements.define("dialog-component", DialogComponent);
class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName, { bubbles: true, composed: true });
  }
  static eventName = "dialog:open";
}
class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName, { bubbles: true, composed: true });
  }
  static eventName = "dialog:close";
}
class SizeGuideComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener("click", this.#onClick);
    this.addEventListener(DialogOpenEvent.eventName, this.#onDialogOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#onDialogClose);
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener("click", this.#onClick);
    this.removeEventListener(DialogOpenEvent.eventName, this.#onDialogOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#onDialogClose);
  }
  get #trigger() {
    return this.querySelector("[data-size-guide-trigger]");
  }
  get #dialog() {
    return this.querySelector("dialog-component");
  }
  #onClick = (event) => {
    const target = event.target;
    if (!target?.closest("[data-size-guide-trigger]")) return;
    this.#dialog?.showDialog?.();
  };
  #onDialogOpen = (event) => {
    if (event.target !== this.#dialog) return;
    this.#trigger?.setAttribute("aria-expanded", "true");
  };
  #onDialogClose = (event) => {
    if (event.target !== this.#dialog) return;
    const trigger = this.#trigger;
    if (!trigger) return;
    trigger.setAttribute("aria-expanded", "false");
    trigger.focus();
  };
}
if (!customElements.get("size-guide-component")) {
  customElements.define("size-guide-component", SizeGuideComponent);
}
document.addEventListener(
  "toggle",
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute("scroll-lock")) {
        const { open } = event.target;
        if (open) {
          document.documentElement.setAttribute("scroll-lock", "");
        } else {
          document.documentElement.removeAttribute("scroll-lock");
        }
      }
    }
  },
  { capture: true }
);
export {
  DialogCloseEvent,
  DialogComponent,
  DialogOpenEvent
};
